// The socket of a room on this screen. It says hello with the token of the
// seat each time it opens, and it opens again by itself after a drop. A phone
// drops the socket of a tab in the background, which is what happens when a
// player goes to another app to send the link. So a drop is normal. It is no
// error, and the screen says only that it is reconnecting.

import { BUILD_ID, ROOM_RECONNECT_FIRST_MS, ROOM_RECONNECT_MOST_MS } from '../constants';
import { type ClientMessage, roomSocketPath, type ServerMessage } from './protocol';

export type SocketStatus = 'connecting' | 'open' | 'reconnecting' | 'closed';

export type RoomSocket = {
  send: (message: ClientMessage) => void;
  /** Ends the socket for good. */
  close: () => void;
};

/** The server ends the room for one of these. The socket does not open again after one. */
const FINAL_MESSAGES: ReadonlySet<ServerMessage['t']> = new Set(['gone', 'old', 'replaced']);

const socketUrl = (code: string): string => {
  const url = new URL(roomSocketPath(code), window.location.href);
  url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
  return url.href;
};

const readServerMessage = (data: unknown): ServerMessage | null => {
  if (typeof data !== 'string') return null;
  try {
    return JSON.parse(data) as ServerMessage;
  } catch {
    return null;
  }
};

export const openRoomSocket = (
  code: string,
  token: string,
  onMessage: (message: ServerMessage) => void,
  onStatus: (status: SocketStatus) => void,
): RoomSocket => {
  let socket: WebSocket | null = null;
  let ended = false;
  let delay = ROOM_RECONNECT_FIRST_MS;
  let timer = 0;
  /** Messages from while the socket was down. They go after the hello, in order. */
  let outbox: ClientMessage[] = [];

  const connect = (): void => {
    if (ended || socket) return;
    window.clearTimeout(timer);
    const next = new WebSocket(socketUrl(code));
    socket = next;
    next.onopen = () => {
      delay = ROOM_RECONNECT_FIRST_MS;
      next.send(JSON.stringify({ t: 'hello', token, build: BUILD_ID } satisfies ClientMessage));
      for (const message of outbox) next.send(JSON.stringify(message));
      outbox = [];
      onStatus('open');
    };
    next.onmessage = (event) => {
      const message = readServerMessage(event.data);
      if (!message) return;
      if (FINAL_MESSAGES.has(message.t)) ended = true;
      onMessage(message);
    };
    next.onclose = () => {
      if (socket === next) socket = null;
      if (ended) {
        onStatus('closed');
        return;
      }
      onStatus('reconnecting');
      timer = window.setTimeout(connect, delay);
      delay = Math.min(delay * 2, ROOM_RECONNECT_MOST_MS);
    };
  };

  // A tab that comes back, or a network that comes back, tries at once.
  const tryNow = (): void => {
    if (document.visibilityState !== 'visible' || ended) return;
    delay = ROOM_RECONNECT_FIRST_MS;
    connect();
  };
  document.addEventListener('visibilitychange', tryNow);
  window.addEventListener('online', tryNow);

  onStatus('connecting');
  connect();

  return {
    send: (message) => {
      if (socket?.readyState === WebSocket.OPEN) socket.send(JSON.stringify(message));
      // An action waits in the match, not here. The sync after the hello sends what the server lacks.
      else if (message.t !== 'act') outbox.push(message);
    },
    close: () => {
      ended = true;
      window.clearTimeout(timer);
      document.removeEventListener('visibilitychange', tryNow);
      window.removeEventListener('online', tryNow);
      socket?.close(1000, 'closed');
      socket = null;
      onStatus('closed');
    },
  };
};
